import type React from "react";
import { memo } from "react";
import { Search, PenLine } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

interface StreamActionToggleProps {
  onSearchMode: () => void;
  onCreateMode: () => void;
  disableSearch?: boolean;
  showCreate?: boolean;
  showSearch?: boolean;
  className?: string;
}

const StreamActionToggleRaw: React.FC<StreamActionToggleProps> = ({
  onSearchMode,
  onCreateMode,
  disableSearch = false,
  showCreate = true,
  showSearch = true,
  className,
}) => {
  const { t } = useTranslation();
  const shouldShowDivider = showCreate && showSearch;

  if (!showCreate && !showSearch) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex h-10 w-full border-b border-gray-100/80 bg-gray-50/60",
        className,
      )}
    >
      {showCreate && (
        <button
          type="button"
          onClick={onCreateMode}
          className="group flex flex-1 cursor-pointer items-center justify-center transition-colors hover:bg-gray-100/50"
        >
          <div className="flex items-center gap-2 text-gray-400 transition-colors group-hover:text-gray-600">
            <PenLine className="size-4" />
            <span className="text-sm font-medium">
              {t("streamSearch.create")}
            </span>
          </div>
        </button>
      )}

      {showSearch && (
        <button
          type="button"
          onClick={onSearchMode}
          disabled={disableSearch}
          className={cn(
            "group flex flex-1 items-center justify-center transition-colors hover:bg-gray-100/50 disabled:cursor-not-allowed disabled:bg-gray-50/40",
            shouldShowDivider && "border-l border-gray-200/70",
          )}
        >
          <div className="flex items-center gap-2 text-gray-400 transition-colors group-hover:text-gray-600 group-disabled:text-gray-300">
            <Search className="size-4" />
            <span className="text-sm font-medium">
              {t("streamSearch.search")}
            </span>
          </div>
        </button>
      )}
    </div>
  );
};

export const StreamActionToggle = memo(StreamActionToggleRaw);
StreamActionToggle.displayName = "StreamActionToggle";
